import axios from "axios";
import React, { useState, useEffect } from "react";
import { BASE_URL } from "../../../env";
import Sidebar from "../../../components/layout/Sidebar";
import { Row, Col, Form } from "react-bootstrap";
import Select from "react-select";
import CustomPagination from "../../../components/ui/CustomPagination";
import DataTable from "../../../components/ui/DataTable";
import { getFirstAndLastEmiDates, formatAmount, generateLoanSummaryForMember,
  resolveEffectiveStartDate,
} from "../../../utils/utils";
import { useAdminAuth } from "../../auth/AdminAuthProvider";
import DashboardHeader from "../../../components/layout/DashboardHeader";
import { MdExpandMore } from "react-icons/md";
import StandardizedDateInput from "../../../components/ui/StandardizedDateInput";

const GplCollectionReport = () => {
  const { adminUser } = useAdminAuth();
  const [reportData, setReportData] = useState([]);
  const [openSidebarToggle, setOpenSidebarToggle] = useState(window.innerWidth > 1000);
  const [currentPage, setCurrentPage] = useState(1);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedBranchFilter, setSelectedBranchFilter] = useState(null);
  const [selectedEmployeeFilter, setSelectedEmployeeFilter] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const itemsPerPage = 20;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;

  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const OpenSidebar = () => setOpenSidebarToggle(!openSidebarToggle);

  useEffect(() => {
    const getReportData = async (id) => {
      try {
        const response = await axios.get(`${BASE_URL}/api/getgplcollectionreport`, {
          params: { id },
        });
        console.log("received data", response.data)
        if (response.data) {
          setReportData(response.data);
        }
      } catch (error) {
        console.error("Error fetching Data:", error);
      }
    };
    getReportData(adminUser.user.id);
  }, [adminUser.user.id]);

  const columns = [
    { label: "S No", render: (data, index) => indexOfFirstItem + index + 1 },
    { label: "Region", render: (data) => data.regionName },
    { label: "Division", render: (data) => data.divisionName },
    { label: "Branch Name", render: (data) => data.branchName },
    { label: "Branch Code", render: (data) => data.branchCode },
    { label: "Customer Name", render: (data) => data.memberName },
    { label: "Customer ID", render: (data) => data.customerId },
    {
      label: "Address",
      render: (data) => {
        const parts = [
          data.permanentDoorNo,
          data.permanentStreetNo,
          data.permanentPanchayat,
          data.permanentTaluk,
          data.permanentDistrict,
          data.permanentPincode
        ].filter(part => part && String(part).trim() !== '');
        return parts.length > 0 ? parts.join(", ") : "N/A";
      },
    },
    { label: "Contact No", render: (data) => data.phoneNumber },
    { label: "Loan Type", render: (data) => data.loanType },
    { label: "Sub Category", render: (data) => data.businessDetails?.natureOfBusiness || "" },
    { label: "Loan Cycle", render: (data) => data.loanCycle },
    { label: "Loan Account No", render: (data) => data.loanId },
    { label: "Rate of Interest", render: (data) => data.proposedLoanDetails?.rateOfInterest || "" },
    {
      label: "Disbursement Date",
      render: (data) =>
        data.branchManagerStatus === "disbursed" && data.branchManagerStatusUpdatedAt
          ? new Date(data.branchManagerStatusUpdatedAt).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" })
          : "",
    },
    {
      label: "EMI Date",
      render: (data) =>
        data.receiptEmiDate
          ? new Date(data.receiptEmiDate).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" })
          : "",
    },
    {
      label: "Maturity Date",
      render: (data) => {
        const { lastEmiDate } = getFirstAndLastEmiDates(
          data.branchManagerStatusUpdatedAt,
          data.proposedLoanDetails?.tenureInMonths,
          data.emiDateByBranchManager,
          resolveEffectiveStartDate(data)
        );
        return lastEmiDate ? lastEmiDate.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" }) : "";
      },
    },
    {
      label: "Loan Amount",
      render: (data) =>
        data.branchManagerStatus === "disbursed"
          ? formatAmount(data.eligibleLoanAmountByBm)
          : "",
    },
    { label: "Employee Name", render: (data) => data.employeeName },
    { label: "Prospect ID", render: (data) => data.ApplicationId },
    { label: "Total Principal Paid", render: (data) => formatAmount(data.totalPrincipalPaid) },
    { label: "Total Interest Paid", render: (data) => formatAmount(data.totalInterestPaid) },
    { label: "Total EMI Paid", render: (data) => formatAmount(data.totalEmiPaid) },
    { label: "EMI Amount", render: (data) => formatAmount(data.receiptEmiAmount) },
    { label: "Received Amount", render: (data) => formatAmount(data.receivedAmount) },
    { label: "Description", render: (data) => data.receiptDescription },
    { label: "Receipt No", render: (data) => data.collectionApproval?.receiptNo || "" },
    {
      label: "Collected Date",
      render: (data) =>
        data.collectedDate
          ? new Date(data.collectedDate).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" })
          : "",
    },
    {
      label: "Collected Time",
      render: (data) =>
        data.collectedDate
          ? new Date(data.collectedDate).toLocaleTimeString("en-GB")
          : "",
    },
    {
      label: "CRO Submitted Date",
      render: (data) =>
        data.collectionApproval?.fieldManagerStatusUpdatedAt
          ? new Date(data.collectionApproval?.fieldManagerStatusUpdatedAt).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" })
          : "",
    },
    {
      label: "CRO Submitted Time",
      render: (data) =>
        data.collectionApproval?.fieldManagerStatusUpdatedAt
          ? new Date(data.collectionApproval?.fieldManagerStatusUpdatedAt).toLocaleTimeString("en-GB")
          : "",
    },
    {
      label: "Branch Manager Approved Date",
      render: (data) =>
        data.collectionApproval?.branchManagerStatusUpdatedAt
          ? new Date(data.collectionApproval?.branchManagerStatusUpdatedAt).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" })
          : "",
    },
    {
      label: "Branch Manager Approved Time",
      render: (data) =>
        data.collectionApproval?.branchManagerStatusUpdatedAt
          ? new Date(data.collectionApproval?.branchManagerStatusUpdatedAt).toLocaleTimeString("en-GB")
          : "",
    },
    {
      label: "MIS Approved Date",
      render: (data) =>
        data.collectionApproval?.misStatusUpdatedAt
          ? new Date(data.collectionApproval?.misStatusUpdatedAt).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" })
          : "",
    },
    {
      label: "MIS Approved Time",
      render: (data) =>
        data.collectionApproval?.misStatusUpdatedAt
          ? new Date(data.collectionApproval?.misStatusUpdatedAt).toLocaleTimeString("en-GB")
          : "",
    },
    {
      label: "Accounts Manager Approved Date",
      render: (data) =>
        data.collectionApproval?.accountManagerStatusUpdatedAt
          ? new Date(data.collectionApproval?.accountManagerStatusUpdatedAt).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" })
          : "",
    },
    {
      label: "Accounts Manager Approved Time",
      render: (data) =>
        data.collectionApproval?.accountManagerStatusUpdatedAt
          ? new Date(data.collectionApproval?.accountManagerStatusUpdatedAt).toLocaleTimeString("en-GB")
          : "",
    },
  ];

  const branchOptions = [...new Set(reportData.map((d) => d?.branchName))].filter(Boolean).map((name) => ({
    value: name,
    label: name,
  }));
  const employeeOptions = [...new Set(reportData.map((d) => d?.username))].filter(Boolean).map((name) => ({
    value: name,
    label: name,
  }));

  const filterData = (data) => {
    let filtered = data;

    if (fromDate && toDate) {
      filtered = filtered.filter((item) => {
        const emiDate = new Date(item.receiptEmiDate);
        const fromDateObj = new Date(fromDate);
        const toDateObj = new Date(toDate);
        fromDateObj.setHours(0, 0, 0, 0);
        toDateObj.setHours(23, 59, 59, 999);
        return emiDate >= fromDateObj && emiDate <= toDateObj;
      });
    }

    if (selectedBranchFilter) {
      filtered = filtered.filter((item) => item?.branchName === selectedBranchFilter.value);
    }

    if (selectedEmployeeFilter) {
      filtered = filtered.filter((item) => item?.username === selectedEmployeeFilter.value);
    }

    if (searchTerm && searchTerm.trim() !== "") {
      const term = searchTerm.trim().toLowerCase();
      filtered = filtered.filter((item) => {
        const memberName = String(item?.memberName || "").toLowerCase();
        const customerId = String(item?.customerId || "").toLowerCase();
        const loanId = String(item?.loanId || "").toLowerCase();
        const phoneNumber = String(item?.phoneNumber || "").toLowerCase();
        const aadharNo = String(item?.aadharNo || "").toLowerCase();
        const applicationId = String(item?.ApplicationId || "").toLowerCase();

        return (
          memberName.includes(term) ||
          customerId.includes(term) ||
          loanId.includes(term) ||
          phoneNumber.includes(term) ||
          aadharNo.includes(term) ||
          applicationId.includes(term)
        );
      });
    }

    return filtered.map((item) => {
      const summary = generateLoanSummaryForMember(item, "2000-01-01", toDate || new Date().toISOString().split("T")[0]);
      return {
        ...item,
        ...summary,
      };
    });
  };

  const totalPages = Math.ceil(filterData(reportData).length / itemsPerPage);
  const pagedData = filterData(reportData).slice(indexOfFirstItem, indexOfLastItem);

  return (
    <section className="master-layout">
      <div className="master-sidebar-wrapper">
        <Sidebar
          openSidebarToggle={openSidebarToggle}
          OpenSidebar={OpenSidebar}
        />
      </div>

      <div className="master-content-wrapper">
        <div className="master-header-wrapper">
          <DashboardHeader OpenSidebar={OpenSidebar} />
        </div>

        <div className="master-card-container">
          <div className="master-search-section">
            <h1 className="TableHeaderText w-100 text-start">Collection Report</h1>
            <div className="w-100">
              <Row className="w-100" style={{ fontWeight: "500" }}>
                <Col xs={12} lg={4} md={6} className="mb-2">
                  <Form.Group>
                    <Form.Label>Search Customer</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Search Name, Customer ID, Loan Acc No, Phone..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col xs={6} lg={2} md={6} className="mb-2">
                  <Form.Group>
                    <Form.Label>From Date</Form.Label>
                    <StandardizedDateInput
                      value={fromDate}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFromDate(val);
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col xs={6} lg={2} md={6} className="mb-2">
                  <Form.Group>
                    <Form.Label>To Date</Form.Label>
                    <StandardizedDateInput
                      value={toDate}
                      onChange={(e) => {
                        const val = e.target.value;
                        setToDate(val);
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>

            <button
              className={`filter-toggle-btn ${showMobileFilters ? "active" : ""}`}
              onClick={() => setShowMobileFilters(!showMobileFilters)}
            >
              <span style={{ fontWeight: 600, fontSize: "14px" }}>
                {showMobileFilters ? "Hide Filters" : "Show Filters"}
              </span>
              <MdExpandMore size={22} />
            </button>

            <div className={`w-100 ${!showMobileFilters ? "mobile-hidden" : "mobile-visible"}`}>
              <Row className="w-100" style={{ fontWeight: "500" }}>

                <Col xs={12} lg={4} md={6} className="mb-2">
                  <Form.Label>Branch</Form.Label>
                  <Select
                    value={selectedBranchFilter}
                    onChange={(val) => {
                      setSelectedBranchFilter(val);
                      if (fromDate && toDate && val && selectedEmployeeFilter) {
                        setTimeout(() => setShowMobileFilters(false), 300);
                      }
                    }}
                    options={branchOptions}
                    isClearable
                    placeholder="Filter by Branch"
                    className="filter-dropdown"
                  />
                </Col>
                <Col xs={12} lg={4} md={6} className="mb-2">
                  <Form.Label>Employee</Form.Label>
                  <Select
                    value={selectedEmployeeFilter}
                    onChange={(val) => {
                      setSelectedEmployeeFilter(val);
                      if (fromDate && toDate && selectedBranchFilter && val) {
                        setTimeout(() => setShowMobileFilters(false), 300);
                      }
                    }}
                    options={employeeOptions}
                    isClearable
                    placeholder="Filter by Employee"
                    className="filter-dropdown"
                  />
                </Col>
              </Row>
            </div>
          </div>

          <div className="master-list-section">
            <div className="master-table-wrapper" style={{ padding: "0 15px" }}>
              <DataTable
                columns={columns}
                data={pagedData}
                reportName="CollectionReport"
                excelData={filterData(reportData)}
              />
            </div>

            <div className="master-pagination-wrapper">
              <CustomPagination
                totalPages={totalPages}
                currentPage={currentPage}
                paginate={paginate}
                totalItems={reportData.length}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GplCollectionReport;
